// スポット通常入札 (SpotBidTable) のステータス列が表示する「12 区分」の導出。
//
// spec AC-BSP-047 は承認状態 (approval_status) + 業務ステータス
// (SpotBid.status = 約定軸) を 1 列に統合した 12 区分を要求する:
//   予約中 / 下書き / 承認待ち / 差戻し / 送信中 / 入札中 / 約定待ち /
//   全約定 / 部分約定 / 未約定 / 取消 / 失効
// AC-BSP-048 は「未約定」(JEPX が評価した結果ゼロ) と「失効」(そもそも JEPX に
// 届かなかった) を必ず別バッジで表示することを要求する。
//
// 判定規則の実体はブロック入札と共通の `bidStatusCategory.ts` に置き、 ここは
// 約定軸 (BiddingSpotBidStatus) の写像と部分約定の細分だけを持つ。
import { BiddingSpotBidStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { CoverLine } from '~/components/bidding/common/types'

import {
  BID_STATUS_CATEGORY_LABEL_KEY,
  type BidMatchOutcome,
  type BidStatusCategoryWithoutPartial,
  deriveBidStatusCategory,
} from './bidStatusCategory'
import { reviewStartedBeforeDeadline } from './needsReview'
import { isSpotPartialMatched } from './spotBidMatchOutcome'
import type { SpotBidRow } from './spotBidRow'

/** AC-BSP-047 の 12 区分。 */
export type SpotBidStatusCategory =
  | BidStatusCategoryWithoutPartial
  | 'partialMatched' // 部分約定

/**
 * AC-BSP-019a: 入札一覧のステータスフィルタの選択肢。
 *
 * [[SpotBidStatusCategory]] の 12 区分から「約定待ち」を除いた 11 区分。 「約定待ち」は
 * 入札締切直後 約 5 分の過渡状態で絞り込んで作業する場面がないため選択肢から除く
 * (ステータス列としては [[deriveSpotBidStatusCategory]] が引き続き返す)。 spec が列挙する
 * 順序 (予約中 / 下書き / 承認待ち / 差戻し / 送信中 / 入札中 / 全約定 / 部分約定 / 未約定 /
 * 取消 / 失効) のまま選択肢に使う。
 */
export const SPOT_BID_STATUS_FILTER_CATEGORIES: readonly SpotBidStatusCategory[] =
  [
    'reserved',
    'draft',
    'pendingApproval',
    'rejected',
    'sending',
    'active',
    'allMatched',
    'partialMatched',
    'unmatched',
    'cancelled',
    'expired',
  ]

/**
 * ステータス列の区分名の i18n key (12 区分)。 入札一覧のステータス列 (SpotBidTable) と
 * 入札一覧の CSV ダウンロードのステータス列 ([[buildNormalBidCsv]]) が同じ
 * 区分名を出すよう、 同じ key を引く (AC-BSP-047。 表示言語が英語なら英語)。 部分約定以外は
 * ブロック入札と共通の [[BID_STATUS_CATEGORY_LABEL_KEY]]。
 */
export const SPOT_BID_STATUS_CATEGORY_LABEL_KEY = {
  ...BID_STATUS_CATEGORY_LABEL_KEY,
  partialMatched: 'page:biddingSpot.bidStatusCategory.partialMatched',
} as const satisfies Record<SpotBidStatusCategory, string>

/** 判定に要る field だけを受ける (= 行全体を組み立てずに単体テストできる)。 */
export type SpotBidStatusCategoryInput = Pick<
  SpotBidRow,
  | 'approvalState'
  | 'bidStatus'
  | 'sendStatus'
  | 'volumeMwh'
  | 'execVolumeMwh'
  | 'receiptChecking'
  | 'workflowId'
  | 'needsReview'
  | 'needsReviewSince'
  | 'targetDate'
>

export const toMatchOutcome = (
  status: BiddingSpotBidStatus,
): BidMatchOutcome => {
  switch (status) {
    case BiddingSpotBidStatus.MATCHED:
      return 'matched'
    case BiddingSpotBidStatus.UNMATCHED:
      return 'unmatched'
    // EXPIRED (AC-BSP-011) は backend が締切到達時に確定させた失効。 FAILED と同じく
    // 権威的な終端値なので isPastDeadline を問わず「失効」 に確定する。
    case BiddingSpotBidStatus.FAILED:
    case BiddingSpotBidStatus.EXPIRED:
      return 'failed'
    default:
      return 'pending'
  }
}

/**
 * 承認状態 + 送信状態 + 約定結果 (bidStatus と数量) の 3 軸から、 ステータス列の
 * 12 区分 ([[SpotBidStatusCategory]]) を 1 つ返す純関数。 規則は
 * [[deriveBidStatusCategory]] を参照。
 */
export const deriveSpotBidStatusCategory = (
  row: SpotBidStatusCategoryInput,
  isPastDeadline: boolean,
): SpotBidStatusCategory => {
  // 取下の完了 (AC-BSP-057) は約定軸の終端値として届くので、 承認軸・送信軸を
  // 問わず「取消」。 取下処理中 / 取下失敗 は板に残っている札なので約定待ちの
  // 札と同じ扱い (= 締切前は「入札中」 のまま、 AC-BSP-057b)。
  if (row.bidStatus === BiddingSpotBidStatus.CANCELLED) {
    return 'cancelled'
  }
  const category = deriveBidStatusCategory(
    {
      approvalState: row.approvalState,
      sendStatus: row.sendStatus,
      matchOutcome: toMatchOutcome(row.bidStatus),
      receiptChecking: row.receiptChecking,
      hasWorkflow: row.workflowId !== undefined && row.workflowId !== '',
    },
    isPastDeadline && !reviewStartedBeforeDeadline(row),
  )
  if (category === 'allMatched' && isSpotPartialMatched(row)) {
    return 'partialMatched'
  }
  return category
}

// ----- 入札後ポジション chart の cover-line 分類 (AC-BSP-108) -----

/**
 * 区分から入札時カバー量の線への写像 (AC-BSP-108)。 `Record` なので区分が増えると型エラーで
 * 追従漏れを検出できる。 時間前の `coverLineOfIntradayStatus` も区分 → 線をこの形で持つ。
 */
const COVER_LINE_OF_SPOT_CATEGORY: Record<SpotBidStatusCategory, CoverLine> = {
  reserved: 'submitted',
  sending: 'submitted',
  active: 'submitted',
  awaitingMatch: 'submitted',
  allMatched: 'submitted',
  partialMatched: 'submitted',
  unmatched: 'submitted',
  draft: 'allOnly',
  pendingApproval: 'allOnly',
  rejected: 'allOnly',
  cancelled: 'excluded',
  expired: 'excluded',
}

export const coverLineOfSpotCategory = (
  category: SpotBidStatusCategory,
): CoverLine => COVER_LINE_OF_SPOT_CATEGORY[category]

/**
 * ステータス列の区分が 「失効」 で、 かつ失効が**確定**しているか。 翌営業日向けの再利用
 * (複製、 AC-BSP-011) と、 「入札締切を超過したため送信できませんでした」 の案内は、
 * 失効が確定してから (AC-BSP-117) に限る。
 *
 * 確定しているかは backend が応答時点で決めた値 ([[SpotBidRow.expiryConfirmed]]) を
 * 読むだけにする。 判定を画面で組み立てて backend と食い違うと、 JEPX が受け付けて
 * いた札を出し直させてしまうため。 区分が 「失効」 でない行は、
 * backend の値に関わらず false (表示中の区分と導線を揃える。 締切の直前直後で
 * 画面の時計と応答時点がずれても、 下書きの行に複製を出さない)。
 *
 * 区分が 「失効」 でも確定していない札がある。 [[deriveBidStatusCategory]] の締切後の
 * 代理判定は、 送信軸が SENT・送信失敗 (再送中) 以外の札を承認軸を問わず 「失効」 に
 * するため、 送信失敗 (確定) の札のうち backend が確かめていない札 (確かめる期限を過ぎて
 * 失効の書込みを待つ札、 送信中のまま手動確認に回った札) も含む。 これらは JEPX が
 * 受け付けていたかを確かめ終えるまで失効ではなく、 backend も確定としない。 確かめている
 * 間の札は区分が 「送信中」 になり ([[SpotBidRow.receiptChecking]])、 ここには来ない。
 */
export const isExpiryConfirmed = (
  row: Pick<SpotBidRow, 'expiryConfirmed'>,
  category: SpotBidStatusCategory,
): boolean => category === 'expired' && row.expiryConfirmed === true
